import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getScreenerData, type ScreenerMetric } from '@/lib/api'
import { boundHistoryRange, bufferedHistoryRange, historyDomain, historyWindowCovers, initialHistoryRange, SCREENER_INTERVAL_SECONDS,
  type ChartTimeRange } from '@/lib/screenerHistory'
import { ScreenerHistoryCache, type ScreenerHistoryScope } from '@/lib/screenerHistoryCache'
import { mergeScreenerTail } from '@/lib/screenerDataMerge'
import { completedHistoryRanges, mergeProgressiveOI, nextHistoryTile, pendingHistoryRanges } from '@/lib/screenerProgressiveHistory'
import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'
import { useScreenerHistoryPriority } from './useScreenerHistoryPriority'

type Snapshot = { compatibility: string; query: string; view: string; data: ScreenerTickerData }

/** The viewport owns navigation; live data can change without moving it. */
export function useScreenerHistory(symbol: string, liveData: ScreenerTickerData | null,
  interval: string, timeframe: ScreenerTimeframe | undefined, exchanges: string[], includeOIOhlc = false,
  markets?: string[], metrics?: ScreenerMetric[], initialStatus?: { loading: boolean; error: string | null },
  sharedCache?: ScreenerHistoryCache) {
  const exchangeKey = [...exchanges].sort().join(',')
  const marketKey = markets ? [...markets].sort().join(',') : undefined
  const metricKey = metrics ? [...metrics].sort().join(',') : undefined
  const compatibility = JSON.stringify([symbol.toUpperCase(), exchangeKey, marketKey, includeOIOhlc])
  const query = JSON.stringify([compatibility, interval, metricKey])
  const view = JSON.stringify([query, timeframe])
  const navigationKey = JSON.stringify([symbol.toUpperCase(), interval, timeframe])
  const followKey = navigationKey
  const [viewport, setViewport] = useState<{ view: string; range: ChartTimeRange | null } | null>(null)
  const [follow, setFollow] = useState<{ query: string; span: number; right: number } | null>(null)
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [loading, setLoading] = useState(false)
  const [failure, setFailure] = useState<{ view: string; message: string } | null>(null)
  const [retry, setRetry] = useState(0)
  const cacheRef = useRef(sharedCache || new ScreenerHistoryCache())
  if (sharedCache) cacheRef.current = sharedCache
  const reuseScope: ScreenerHistoryScope = { symbol, exchanges, markets, metrics, includeOIOhlc }
  const invalidationsRef = useRef(new Map<string, number>())
  const repairRef = useRef<string | null>(null)
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const receivedRef = useRef<{ data: ScreenerTickerData; query: string } | null>(null)
  const completedResponseRef = useRef<{ view: string; load: ScreenerTickerData['oi_history_load'] } | null>(null)
  const resolutionPresentationRef = useRef<{ view: string; previous: Snapshot } | null>(null)
  // A parent render can momentarily contain the previous request's object while
  // its layout effect clears data. Never relabel that object as a new scope.
  if (liveData && receivedRef.current?.data !== liveData) receivedRef.current = { data: liveData, query }
  const currentLive = liveData && receivedRef.current?.query === query
    && liveData.symbol.toUpperCase() === symbol.toUpperCase()
    && (liveData.oi_include_ohlc ?? false) === includeOIOhlc ? liveData : null
  const retained = snapshot?.compatibility === compatibility ? snapshot.data : null
  const initial = initialHistoryRange(currentLive || retained, interval, timeframe)
  const absoluteFrom = timeframe?.mode === 'absolute' ? initial.from : undefined
  const absoluteTo = timeframe?.mode === 'absolute' ? initial.to : undefined
  const selection = useMemo(() => viewport?.view === navigationKey ? viewport.range
    : absoluteFrom !== undefined && absoluteTo !== undefined ? { from: absoluteFrom, to: absoluteTo } : null,
  [viewport, navigationKey, absoluteFrom, absoluteTo])
  const centered = follow?.query === followKey ? follow : null
  const latest = initialHistoryRange(currentLive || retained, interval, { mode: 'live' }).to
  const range = selection || (centered
    ? { from: latest - centered.span * (1 - centered.right), to: latest + centered.span * centered.right }
    : initial)
  const cached = cacheRef.current.get(query, range, interval)
  const viewStateRef = useRef({ range, selection, manuallyNavigated: viewport?.view === navigationKey })
  viewStateRef.current = { range, selection, manuallyNavigated: viewport?.view === navigationKey }
  const initialDataReady = currentLive !== null
  const commit = useCallback((data: ScreenerTickerData) => {
    cacheRef.current.put(query, data, viewStateRef.current.range.to - viewStateRef.current.range.from,
      { ...reuseScope, metrics: (metrics || ['price', 'oi', 'volume', 'cvd', 'funding']).filter(metric => !data.pending_metrics?.includes(metric)) })
    const next = { compatibility, query, view, data }
    snapshotRef.current = next
    setSnapshot(next)
  }, [compatibility, query, view])

  const invalidationGeneration = currentLive?.history_invalidation?.generation
  const invalidationFrom = currentLive?.history_invalidation?.from_ts
  useEffect(() => {
    if (invalidationGeneration === undefined
      || invalidationsRef.current.get(symbol) === invalidationGeneration) return
    invalidationsRef.current.set(symbol, invalidationGeneration)
    cacheRef.current.invalidate(symbol, invalidationFrom)
    const previous = snapshotRef.current
    const progress = previous?.query === query ? previous.data.oi_history_load : undefined
    if (progress) {
      const completed = invalidationFrom == null ? [] : progress.completed.flatMap(range => {
        const to = Math.min(range.to_ts, invalidationFrom)
        return to > range.from_ts ? [{ from_ts: range.from_ts, to_ts: to }] : []
      })
      const pending = pendingHistoryRanges(progress.requested, completed)
      const authorities = invalidationFrom == null ? [] : previous!.data.oi_history_authorities?.flatMap(range => {
        const to = Math.min(range.to_ts, invalidationFrom)
        return to > range.from_ts ? [{ ...range, to_ts: to }] : []
      })
      commit({ ...previous!.data, oi_history_authorities: authorities, oi_history_load: { ...progress, completed, pending,
        status: pending.length ? 'loading' : 'complete', error: undefined } })
    }
    const selection = viewStateRef.current.selection
    if (selection && (invalidationFrom == null || selection.to >= invalidationFrom)) repairRef.current = query
    setRetry(value => value + 1)
  }, [invalidationGeneration, invalidationFrom, query, symbol])

  useEffect(() => {
    if (!currentLive) return
    cacheRef.current.observeEpoch(currentLive)
    const { range, selection, manuallyNavigated } = viewStateRef.current
    const span = range.to - range.from
    const retainedSnapshot = snapshotRef.current
    if (retainedSnapshot?.compatibility === compatibility && retainedSnapshot.query !== query
      && retainedSnapshot.data.oi_interval !== currentLive.oi_interval
      && retainedSnapshot.data.oi_revision_epoch === currentLive.oi_revision_epoch) {
      resolutionPresentationRef.current = { view, previous: retainedSnapshot }
    }
    const reusable = retainedSnapshot?.query !== query || retainedSnapshot.view !== view
      ? cacheRef.current.reusable(reuseScope, bufferedHistoryRange(range), interval, currentLive.oi_revision_epoch) : null
    const selected = reusable ? { compatibility, query, view, data: { ...reusable.data,
      pending_metrics: [...new Set([...(reusable.data.pending_metrics || []), ...reusable.missingMetrics])] } } : retainedSnapshot
    const incomingWindow = currentLive.history_window
    const selectedWindow = selected?.data.history_window
    const fillsSelected = incomingWindow && selectedWindow && historyWindowCovers(
      { from: incomingWindow.from_ts, to: incomingWindow.to_ts }, { from: selectedWindow.from_ts, to: selectedWindow.to_ts })
    const metricFields: Record<string, string[]> = { oi: ['oi_history'], price: ['candles', 'trade_price_history'],
      volume: ['volume_history'], cvd: ['cvd_history'], funding: ['funding_rate'] }
    const includesMetric = (metric: string) => Object.values(currentLive.exchanges).some(exchange =>
      (metricFields[metric] || []).some(field => Object.prototype.hasOwnProperty.call(exchange, field)))
    const previous = selected && currentLive.pending_metrics !== undefined ? { ...selected, data: { ...selected.data,
      pending_metrics: [...new Set([...(selected.data.pending_metrics || []).filter(metric => !fillsSelected || !includesMetric(metric)
        || currentLive.pending_metrics!.includes(metric)), ...currentLive.pending_metrics])] } } : selected
    const incomingProgress = currentLive.oi_history_load
    const freshCompletion = !!incomingProgress && (completedResponseRef.current?.view !== view
      || completedResponseRef.current.load !== incomingProgress)
    if (incomingProgress) completedResponseRef.current = { view, load: incomingProgress }
    const completionSource = { ...currentLive, oi_read_started_at: currentLive.read_started_at ?? currentLive.oi_read_started_at }
    const epochChanged = previous?.query === query && previous.data.oi_revision_epoch && currentLive.oi_revision_epoch
      && previous.data.oi_revision_epoch !== currentLive.oi_revision_epoch
    if (epochChanged) { commit(currentLive); return }
    const replaceCvd = incomingWindow && historyWindowCovers({ from: incomingWindow.from_ts, to: incomingWindow.to_ts },
      { from: range.from, to: Math.min(range.to, Date.now() / 1000) }) ? true : undefined
    if (selection && repairRef.current === query) {
      const window = currentLive.history_window
      if (window && !window.truncated && historyWindowCovers({ from: window.from_ts, to: window.to_ts }, {
        from: selection.from, to: Math.min(selection.to, currentLive.oi_history_until || window.to_ts),
      })) {
        repairRef.current = null
        const previous = snapshotRef.current
        if (currentLive.oi_history_load && previous?.query === query) {
          commit(mergeProgressiveOI(previous.data, completionSource, exchangeKey ? exchangeKey.split(',') : []))
        } else commit(currentLive) // A correction can remove points; never union an authoritative replacement.
      }
      return
    }
    if (!selection || !manuallyNavigated || previous?.query !== query) {
      if (!selection) repairRef.current = null
      const progress = previous?.query === query ? previous.data.oi_history_load : undefined
      if (progress && incomingProgress && previous?.data.oi_interval === currentLive.oi_interval) {
        // The primary owns live tails, while this hook owns older OI tiles.
        // A later live snapshot must not discard already completed older tiles.
        const merged = mergeScreenerTail(previous!.data, currentLive, { replaceCvd, allowNewExchanges: true, maxPoints: 5000 })
        if (progress.requested.from_ts === incomingProgress.requested.from_ts
          && progress.requested.to_ts === incomingProgress.requested.to_ts) {
          const retained = { ...merged, oi_history_load: progress }
          commit(freshCompletion ? mergeProgressiveOI(retained, completionSource, exchangeKey ? exchangeKey.split(',') : []) : retained)
        }
        else {
          const completed = completedHistoryRanges(progress.completed, incomingProgress.requested)
          const pending = pendingHistoryRanges(incomingProgress.requested, completed)
          commit(mergeProgressiveOI({ ...merged, oi_history_load: { ...incomingProgress, completed, pending } },
            completionSource, exchangeKey ? exchangeKey.split(',') : []))
        }
      } else if (incomingProgress && previous?.query === query && previous.data.oi_interval === currentLive.oi_interval) {
        const merged = mergeScreenerTail(previous.data, currentLive, { replaceCvd, allowNewExchanges: true, maxPoints: 5000 })
        commit(mergeProgressiveOI({ ...merged, oi_history_load: { ...incomingProgress, completed: [] } },
          completionSource, exchangeKey ? exchangeKey.split(',') : []))
      } else if (previous?.query === query && previous.view === view
        && previous.data.oi_interval === currentLive.oi_interval
        && (!previous.data.oi_revision_epoch || !currentLive.oi_revision_epoch
          || previous.data.oi_revision_epoch === currentLive.oi_revision_epoch)
        && (invalidationGeneration === undefined
          || previous.data.history_invalidation?.generation === invalidationGeneration)) {
        // Raw snapshots and metric-only refreshes have no progressive marker.
        // Their smaller live buffer must not replace history loaded by this view.
        commit(mergeScreenerTail(previous.data, currentLive, { allowNewExchanges: true, maxPoints: 5000 }))
      } else commit(currentLive)
      return
    }
    // Older inspected windows remain stable. Overlapping live observations and
    // corrections update the current window without changing its coordinates.
    const window = previous.data.history_window
    const firstLive = Math.min(...Object.values(currentLive.exchanges).flatMap(data =>
      [data.oi_history?.[0]?.timestamp, data.trade_price_history?.[0]?.timestamp, data.candles?.[0]?.timestamp,
        data.volume_history?.[0]?.timestamp, data.cvd_history?.[0]?.timestamp]
        .filter((timestamp): timestamp is number => timestamp !== undefined)), Infinity)
    const authority = currentLive.oi_authoritative_window
    const overlapsAuthority = authority && range.to >= authority.from_ts && range.from <= authority.to_ts
    const overlapsCompletion = freshCompletion && incomingProgress.completed.some(completed =>
      range.to >= completed.from_ts && range.from < completed.to_ts)
    if ((overlapsAuthority || overlapsCompletion || (range.to >= firstLive && range.from <= (currentLive.oi_history_until || Date.now() / 1000)))
      && previous.data.oi_interval === currentLive.oi_interval) {
      const merged = mergeScreenerTail(previous.data, currentLive, {
        from: window?.from_ts ?? range.from, to: Math.max(window?.to_ts ?? 0, Math.min(Date.now() / 1000, range.to)), maxPoints: 5000,
      })
      const updated = freshCompletion ? mergeProgressiveOI(merged, completionSource, exchangeKey ? exchangeKey.split(',') : []) : merged
      commit(updated)
    }
  }, [currentLive, query, commit])

  useEffect(() => {
    if (!selection) return
    const loaded = snapshotRef.current
    const loadedWindow = loaded?.data.history_window
    const recorded = { from: selection.from, to: selection.to > Date.now() / 1000 && loadedWindow
      ? Math.min(Date.now() / 1000, loadedWindow.to_ts, loaded?.data.oi_history_load?.requested.to_ts ?? Infinity)
      : selection.to }
    if (loaded?.query === query && loaded.view === view && loaded.data.oi_history_load && loadedWindow
      && (!currentLive?.oi_revision_epoch || !loaded.data.oi_revision_epoch
        || currentLive.oi_revision_epoch === loaded.data.oi_revision_epoch)
      && (loaded.data.oi_interval === interval || selection.to - selection.from >= (loadedWindow.to_ts - loadedWindow.from_ts) / 2)
      && recorded.to > recorded.from && historyWindowCovers({ from: loadedWindow.from_ts, to: loadedWindow.to_ts }, recorded)) {
      // Pending OI and invalidated OI ranges belong to the tile queue. Repeating
      // the initial read would replace accumulated history with its newest tile.
      repairRef.current = null
      return
    }
    const known = cacheRef.current.get(query, selection, interval)
    if (known) { repairRef.current = null; commit(known); setFailure(null); return }
    // The matching full request owns initial hydration. Navigation can also use
    // cached data while that request is still in flight.
    if (!initialDataReady) return
    if (selection.from >= Date.now() / 1000) return // Entirely empty future space.
    const controller = new AbortController()
    const requested = bufferedHistoryRange(selection)
    if (requested.to <= requested.from) return
    const timer = setTimeout(async () => {
      setLoading(true)
      setFailure(null)
      try {
        const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: requested.from, to_ts: requested.to },
          interval, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [], requested, includeOIOhlc,
          { includeFallback: false, markets: marketKey === undefined ? undefined : marketKey ? marketKey.split(',') : [],
            oiProgressive: metricKey === undefined || metricKey.split(',').includes('oi'),
            metrics: metricKey === undefined ? undefined : metricKey ? metricKey.split(',') as ScreenerMetric[] : [] })
        if (controller.signal.aborted) return
        if (!result.ok || !result.data) throw new Error(result.error || 'Unable to load chart history')
        if (result.data.history_window?.truncated) throw new Error('This range contains too many points. Choose a larger interval or a shorter range.')
        if (result.data.symbol.toUpperCase() !== symbol.toUpperCase()) throw new Error('Received history for another symbol')
        const previous = snapshotRef.current
        let data = result.data
        if (data.oi_history_load && previous?.query === query && previous.view === view
          && previous.data.oi_interval === data.oi_interval
          && (!previous.data.oi_revision_epoch || !data.oi_revision_epoch
            || previous.data.oi_revision_epoch === data.oi_revision_epoch)) {
          const requested = data.oi_history_load.requested
          const completed = completedHistoryRanges(previous.data.oi_history_load?.completed || [], requested)
          const merged = mergeScreenerTail(previous.data, data, { from: requested.from_ts, to: requested.to_ts,
            allowNewExchanges: true, replaceCvd: true, maxPoints: 5000 })
          data = mergeProgressiveOI({ ...merged, history_window: data.history_window,
            history_invalidation: data.history_invalidation ?? previous.data.history_invalidation,
            oi_history_load: { ...data.oi_history_load, completed, pending: pendingHistoryRanges(requested, completed) } },
          data, exchangeKey ? exchangeKey.split(',') : [])
        }
        repairRef.current = null
        commit(data)
      } catch (cause) {
        if (!controller.signal.aborted) setFailure({ view, message: cause instanceof Error ? cause.message : 'Unable to load chart history' })
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 180)
    return () => { clearTimeout(timer); controller.abort(); setLoading(false) }
  }, [selection, view, query, symbol, interval, exchangeKey, marketKey, metricKey,
    includeOIOhlc, retry, initialDataReady, commit])

  // One owner fills OI history for both relative and fixed views. These reads
  // are small, sequential and independent of the primary live-tail cadence.
  const progress = snapshot?.query === query && snapshot.view === view ? snapshot.data.oi_history_load : undefined
  useScreenerHistoryPriority(view, symbol, exchanges, snapshot?.data.oi_interval || interval, range, progress?.pending,
    (metricKey === undefined || metricKey.split(',').includes('oi'))
      && (marketKey === undefined || marketKey.split(',').includes('futures')))
  const progressKey = progress ? JSON.stringify([query, snapshot?.data.oi_revision_epoch, progress.requested]) : null
  useEffect(() => {
    if (!progressKey) return
    let stopped = false
    let controller: AbortController | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    const stepAtStart = SCREENER_INTERVAL_SECONDS[snapshotRef.current?.data.oi_interval || interval] || 60
    let tileSeconds = progress?.split_required && progress.failed
      ? Math.max(stepAtStart, Math.floor((progress.failed.to_ts - progress.failed.from_ts) / 2 / stepAtStart) * stepAtStart) : 3600
    let tileCeiling = progress?.split_required ? tileSeconds : 7 * 86400
    let fastSuccesses = 0
    let failures = 0
    const active = () => {
      const current = snapshotRef.current
      const visible = viewStateRef.current.selection
      const window = current?.data.history_window
      const visiblePast = visible && window ? { from: visible.from,
        to: Math.min(visible.to, Date.now() / 1000, window.to_ts,
          current.data.oi_history_load?.requested.to_ts ?? window.to_ts) } : null
      return current?.query === query && current.view === view
        && (!visible || (window && visiblePast && visiblePast.to > visiblePast.from
          && historyWindowCovers({ from: window.from_ts, to: window.to_ts }, visiblePast)))
        && JSON.stringify([query, current.data.oi_revision_epoch, current.data.oi_history_load?.requested]) === progressKey
        ? current.data : null
    }
    const block = (message: string) => {
      const current = active()
      if (current?.oi_history_load) commit({ ...current, oi_history_load: {
        ...current.oi_history_load, status: 'blocked', error: message } })
    }
    const schedule = (delay: number) => { if (!stopped) timer = setTimeout(read, Math.min(60_000, Math.max(100, delay))) }
    const read = async () => {
      const current = active()
      const load = current?.oi_history_load
      if (stopped || !current || !load || load.status === 'complete' || load.status === 'blocked') return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { schedule(1000); return }
      const effective = current.oi_interval || interval
      const step = SCREENER_INTERVAL_SECONDS[effective] || 60
      const tile = nextHistoryTile(load, step, tileSeconds)
      if (!tile) return
      controller = new AbortController()
      const started = Date.now()
      try {
        const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: tile.from_ts, to_ts: tile.to_ts },
          effective, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [],
          { from: tile.from_ts, to: tile.to_ts }, includeOIOhlc,
          { includeFallback: false, metrics: ['oi'], limit: 5000, oiProgressive: true,
            oiTileSeconds: tileSeconds, markets: marketKey === undefined ? undefined : marketKey ? marketKey.split(',') : [] })
        if (stopped || controller.signal.aborted || !active()) return
        const state = result.data?.oi_history_load || result.oi_history_load
        if (state?.split_required) {
          const failed = state.failed || tile
          const span = failed.to_ts - failed.from_ts
          if (span <= step) { block(state.error || 'One history bucket could not be loaded. Retry when the database is available.'); return }
          tileSeconds = Math.max(step, Math.floor(Math.min(tileSeconds, span) / 2 / step) * step)
          tileCeiling = Math.min(tileCeiling, tileSeconds)
          fastSuccesses = 0
          failures += 1
        } else if (!result.ok || !result.data || !state) {
          if (state?.status === 'blocked') { block(state.error || result.error || 'History is unavailable'); return }
          throw new Error(result.error || state?.error || 'Unable to load older history')
        } else {
          const updated = mergeProgressiveOI(active()!, result.data, exchangeKey ? exchangeKey.split(',') : [])
          const advanced = JSON.stringify(updated.oi_history_load?.completed) !== JSON.stringify(load.completed)
          failures = advanced ? 0 : failures + 1
          fastSuccesses = advanced && Date.now() - started < 500 ? fastSuccesses + 1 : 0
          if (advanced) {
            // A failed size stays capped until several fast smaller reads prove
            // we may have reached a cheaper retained tier. Never bounce straight
            // from a successful split back into the same expensive query.
            if (tileSeconds >= tileCeiling && fastSuccesses >= 4) {
              tileCeiling = Math.min(7 * 86400, tileCeiling * 2)
              fastSuccesses = 0
            }
            tileSeconds = Math.min(tileCeiling, Math.max(step, tileSeconds * 2))
          }
          commit(updated)
          if (updated.oi_history_load?.status === 'complete') {
            return
          }
          if (updated.oi_history_load?.status === 'blocked') return
        }
        if (failures >= 8) { block(state?.error || 'Older history is not ready. Retry to continue.'); return }
        schedule(Math.max(state?.retry_after_ms || 100, failures ? Math.min(60_000, 1000 * 2 ** (failures - 1)) : 100))
      } catch (cause) {
        if (stopped || controller.signal.aborted) return
        fastSuccesses = 0
        if (++failures >= 5) { block(cause instanceof Error ? cause.message : 'Unable to load older history'); return }
        schedule(Math.min(60_000, 1000 * 2 ** failures))
      }
    }
    schedule(progress?.retry_after_ms || 100)
    return () => { stopped = true; if (timer !== null) clearTimeout(timer); controller?.abort() }
  }, [progressKey, query, view, symbol, interval, exchangeKey, marketKey, includeOIOhlc, retry,
    invalidationGeneration, commit])

  // CVD cannot be appended to an independently anchored cumulative history.
  // Refresh that metric alone for pinned windows touching the live edge; OI,
  // price and volume continue on the small revision-aware live path above.
  const effectiveCvdInterval = snapshot?.query === query ? snapshot.data.oi_interval : interval
  const followingCvdWindow = !selection && viewport?.view === navigationKey && snapshot?.query === query && snapshot.view === view
    ? snapshot.data.history_window : undefined
  const followingCvdFrom = followingCvdWindow?.from_ts
  const followingCvdTo = followingCvdWindow?.to_ts
  const cvdSelection = useMemo(() => selection || (followingCvdFrom !== undefined && followingCvdTo !== undefined
    ? { from: followingCvdFrom, to: followingCvdTo } : null), [selection, followingCvdFrom, followingCvdTo])
  const followingCvd = followingCvdWindow !== undefined
  useEffect(() => {
    if (!cvdSelection || !initialDataReady || (metricKey !== undefined && !metricKey.split(',').includes('cvd'))
      || (!followingCvd && cvdSelection.to < Date.now() / 1000 - 60)) return
    let stopped = false
    let controller: AbortController | null = null
    let timer: ReturnType<typeof setTimeout>
    const refreshContext = () => {
      const current = snapshotRef.current
      const window = current?.query === query ? current.data.history_window : undefined
      const buffered = window ? { from: window.from_ts, to: window.to_ts } : bufferedHistoryRange(cvdSelection)
      const effectiveInterval = current?.query === query ? current.data.oi_interval : interval
      const to = Math.floor(Math.min(Date.now() / 1000, followingCvd ? Infinity : Math.max(buffered.to, cvdSelection.to)))
      const step = SCREENER_INTERVAL_SECONDS[effectiveInterval || interval] || 60
      const requested = { from: followingCvd ? Math.max(buffered.from, Math.ceil((to - 4999 * step) / step) * step) : buffered.from, to }
      const fast = effectiveInterval === '5s' && Math.floor((requested.to - requested.from) / 5) + 1 <= 500
        && Object.values(current?.data.exchanges || {}).every(data => !data.cvd_interval || data.cvd_interval === '5s')
      return { current, window, requested, effectiveInterval, fast }
    }
    const delay = () => refreshContext().fast ? 2_000 : 15_000
    const refreshCvd = async () => {
      const { current, window, requested, effectiveInterval, fast } = refreshContext()
      if (!stopped && repairRef.current !== query && current && window && window.from_ts <= cvdSelection.from
        && Object.values(current.data.exchanges).some(data => data.cvd_history !== undefined)
        && (typeof document === 'undefined' || document.visibilityState !== 'hidden')) {
        controller = new AbortController()
        try {
          const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: requested.from, to_ts: requested.to },
            effectiveInterval, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [], requested, includeOIOhlc,
            { includeFallback: false, limit: fast ? 500 : 5000,
              markets: marketKey === undefined ? undefined : marketKey ? marketKey.split(',') : [], metrics: ['cvd'] })
          const latestSnapshot = snapshotRef.current
          if (!stopped && !controller.signal.aborted && result.ok && result.data
            && !result.data.history_window?.truncated && latestSnapshot?.query === query) {
            const merged = mergeScreenerTail(latestSnapshot.data, result.data, { replaceCvd: true, maxPoints: 5000 })
            commit(merged)
          }
        } catch { /* Keep the last complete CVD snapshot; the next bounded read retries. */ }
      }
      if (!stopped) timer = setTimeout(refreshCvd, delay())
    }
    timer = setTimeout(refreshCvd, delay())
    return () => { stopped = true; clearTimeout(timer); controller?.abort() }
  }, [cvdSelection, followingCvd, initialDataReady, view, query, interval, symbol, exchangeKey,
    marketKey, metricKey, includeOIOhlc, commit, effectiveCvdInterval])

  const navigate = useCallback((next: ChartTimeRange) => {
    if (!Number.isFinite(next.from) || !Number.isFinite(next.to) || next.to <= next.from) return
    next = boundHistoryRange(next, interval)
    setViewport(current => current?.view === navigationKey && current.range
      && Math.abs(current.range.from - next.from) < 0.01 && Math.abs(current.range.to - next.to) < 0.01
      ? current : { view: navigationKey, range: next })
  }, [navigationKey, interval])
  const goLive = useCallback(() => {
    repairRef.current = null
    setViewport({ view: navigationKey, range: null })
    setFollow(null)
    setFailure(null)
  }, [navigationKey])
  const centerCurrent = useCallback(() => {
    repairRef.current = null
    setViewport({ view: navigationKey, range: null })
    setFollow({ query: followKey, span: range.to - range.from, right: 0.5 })
    setFailure(null)
  }, [navigationKey, followKey, range.to, range.from])
  const loadedSnapshot = snapshot?.query === query && snapshot.view === view ? snapshot.data : null
  const resolved = loadedSnapshot || (selection ? cached || retained || currentLive : currentLive || cached || retained)
  const presentation = resolutionPresentationRef.current
  const previousResolution = presentation?.view === view && presentation.previous.compatibility === compatibility
    && presentation.previous.data.oi_revision_epoch === resolved?.oi_revision_epoch ? presentation.previous.data : null
  let oldFrom = Infinity
  let oldTo = -Infinity
  if (previousResolution) for (const data of Object.values(previousResolution.exchanges)) for (const point of data.oi_history || []) {
    if (point.timestamp < range.from || point.timestamp > range.to) continue
    oldFrom = Math.min(oldFrom, point.timestamp)
    oldTo = Math.max(oldTo, point.timestamp + (SCREENER_INTERVAL_SECONDS[previousResolution.oi_interval || '1m'] || 60))
  }
  const awaitingResolution = !!previousResolution && !!resolved?.oi_history_load?.pending.some(pending =>
    pending.from_ts < oldTo && pending.to_ts > oldFrom)
  if (presentation && !awaitingResolution) resolutionPresentationRef.current = null
  const displayed = awaitingResolution ? previousResolution : resolved
  const domain = historyDomain(range, displayed?.history_window?.earliest_ts)
  const { from, to } = range
  const { from: domainFrom, to: domainTo } = domain
  const navigation = useMemo(() => ({ range: { from, to }, domain: { from: domainFrom, to: domainTo }, navigate }),
    [from, to, domainFrom, domainTo, navigate])
  const initialError = !currentLive ? initialStatus?.error : null
  return { data: displayed, navigation, browsing: selection !== null, centered: !selection && !!centered,
    showingPrevious: awaitingResolution || (!!displayed && !currentLive && !cached),
    loading: loading || (awaitingResolution && resolved?.oi_history_load?.status === 'loading')
      || repairRef.current === query || (!currentLive && !cached && !initialError && initialStatus?.loading !== false),
    error: initialError || (resolved?.oi_history_load?.status === 'blocked' ? resolved.oi_history_load.error || 'Older history is unavailable' : null)
      || (failure?.view === view ? failure.message : null),
    goLive, centerCurrent, retry: () => {
      const current = snapshotRef.current
      if (current?.query === query && current.data.oi_history_load?.status === 'blocked') commit({ ...current.data,
        oi_history_load: { ...current.data.oi_history_load, status: 'loading', error: undefined } })
      setRetry(value => value + 1)
    } }
}
